import { useRef } from "react";
import { clsx } from "clsx";

import { useScrollPosition } from "~/hooks/useScrollPosition";

import { When } from "../when/When";
import { Icon } from "../icon/Icon";

import type { Tab } from "./Tab";
import type { CommonProps } from "../components";

type TabListChild = React.ReactElement<typeof Tab>;

export interface TabListProps extends CommonProps {
  children?: TabListChild | TabListChild[];
  role?: "tablist" | "navigation";
  onClick?: (e: React.SyntheticEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

// Arbitrary distance to scroll by when a user clicks the
// scroll left/right buttons
const SCROLL_DISTANCE = 160;

export function TabList({
  children,
  className,
  role,
  onClick,
  onKeyDown,
  ...rest
}: TabListProps) {
  const [scrollRef, { atStart, atEnd }] = useScrollPosition<HTMLDivElement>();

  // Used to track the state of the drag operation
  const dragStateRef = useRef({
    down: false,
    dragging: false,
    scrollLeft: 0,
    startX: 0,
  });

  const handleLeftClick = () => {
    const node = scrollRef.current;

    if (!node) return;

    // Calculate the maximum distance that can be scrolled
    const distance = Math.min(
      SCROLL_DISTANCE,
      SCROLL_DISTANCE - Math.abs(node.scrollLeft - SCROLL_DISTANCE),
    );

    scrollRef.current?.scrollBy({
      left: -1 * distance,
      behavior: "smooth",
    });
  };

  const handleRightClick = () => {
    const node = scrollRef.current;

    if (!node) return;

    // Calculate the maximum distance that can be scrolled
    const distance = Math.min(
      SCROLL_DISTANCE,
      SCROLL_DISTANCE -
        Math.abs(
          node.scrollWidth -
            node.clientWidth -
            node.scrollLeft -
            SCROLL_DISTANCE,
        ),
    );

    scrollRef.current?.scrollBy({
      left: distance,
      behavior: "smooth",
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const scroller = scrollRef.current;

    if (!scroller) return;

    dragStateRef.current = {
      down: true,
      startX: e.pageX - scroller.offsetLeft,
      scrollLeft: scroller.scrollLeft,
      dragging: false,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const scroller = scrollRef.current;
    const state = dragStateRef.current;

    if (!scroller || !state.down) return;

    const x = e.pageX - scroller.offsetLeft;
    const walk = x - state.startX;

    scroller.scrollLeft = state.scrollLeft - walk;

    dragStateRef.current = {
      ...dragStateRef.current,
      dragging: true,
    };
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!dragStateRef.current.dragging) {
      onClick?.(e);
    }

    dragStateRef.current = {
      ...dragStateRef.current,
      down: false,
      dragging: false,
    };
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    dragStateRef.current = {
      ...dragStateRef.current,
      down: false,
      dragging: false,
    };
  };

  return (
    <div
      className={clsx("TabList", className)}
      data-overflow={clsx({ start: !atStart, end: !atEnd })}
      role={role}
      onKeyDown={onKeyDown}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <When condition={!atStart}>
        <div className="TabList-button">
          <button aria-hidden="true" tabIndex={-1} onClick={handleLeftClick}>
            <Icon icon="angle-left" size="md" />
          </button>
        </div>
      </When>

      <div ref={scrollRef} className="TabList-tabs">
        {children}
      </div>

      <When condition={!atEnd}>
        <div className="TabList-button">
          <button aria-hidden="true" tabIndex={-1} onClick={handleRightClick}>
            <Icon icon="angle-right" size="md" />
          </button>
        </div>
      </When>
    </div>
  );
}
